// src/App.js - Complete with Subdomain Support

import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate  } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { TempStoreProvider } from './contexts/TempStoreContext';
import { ActiveShopProvider } from './contexts/ActiveShopContext';
import { LocationProvider } from './contexts/LocationContext';
import ProtectedRoute from './components/auth/ProtectedRoute';
import NavMenu from './pages/shop/components/NavMenu';
import BottomNavigation from './components/BottomNavigation/BottomNavigation';
import { DEFAULT_THEME } from './theme/config/themes';
import 'leaflet/dist/leaflet.css';
import './styles/leaflet-fix.css';
import { GlobalStyles } from './styles/GlobalStyles';
import { parseSubdomain, redirectToMainApp, isSubdomainAllowedRoute } from './utils/subdomainRouter';
import PWASessionStatus from './components/PWASessionStatus';

// Pages
import WelcomePage from './pages/WelcomePage';
import ShopPage from './pages/shop/ShopPage';
import LiveShopCreation from './pages/shop/LiveShopCreation';
import AuthPage from './pages/auth/AuthPage';
import VerifyEmail from './pages/auth/VerifyEmail';
import ShopDashboard from './pages/shop/ShopDashboard';
import ProfilePage from './pages/shop/ProfilePage.js';
import NotificationsPage from './pages/shop/NotificationsPage.js';
import ShopPublicView from './pages/shop/ShopPublicView.js';
import MessagesPage from './pages/shop/MessagesPage';

import { Analytics } from "@vercel/analytics/react"

import NotificationService from './services/NotificationService';
import { auth } from './firebase/config';


// Updated SubdomainHandler Component for App.js

// src/App.js - Update SubdomainHandler component

const SubdomainHandler = ({ shopUsername }) => {
  const [shopId, setShopId] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchShopByUsername = async () => {
      try {
        console.log('🔍 Searching for shop with username:', shopUsername);
        
        const { collection, query, where, getDocs } = await import('firebase/firestore');
        const { db } = await import('./firebase/config');
        
        // Query shops collection for matching username
        const shopsRef = collection(db, 'shops');
        const q = query(shopsRef, where('username', '==', shopUsername));
        
        console.log('📡 Executing Firestore query...');
        const querySnapshot = await getDocs(q);
        
        console.log('📊 Query results:', {
          empty: querySnapshot.empty,
          size: querySnapshot.size,
          docs: querySnapshot.docs.map(doc => ({ id: doc.id, username: doc.data().username }))
        });
        
        if (querySnapshot.empty) {
          console.error('❌ No shop found with username:', shopUsername);
          setError(`Shop "${shopUsername}" not found`);
          return;
        }
        
        // Get first matching shop
        const shopDoc = querySnapshot.docs[0];
        console.log('✅ Shop found:', {
          id: shopDoc.id,
          data: shopDoc.data()
        });
        
        setShopId(shopDoc.id);
        
      } catch (err) {
        console.error('💥 Error fetching shop by username:', err);
        setError('Failed to load shop: ' + err.message);
      }
    };

    if (shopUsername) {
      fetchShopByUsername();
    } else {
      console.error('❌ No shopUsername provided to SubdomainHandler');
      setError('Invalid shop URL');
    }
  }, [shopUsername]);

  // REMOVED: Loading state entirely
  // Show nothing while loading, then redirect
  if (error) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        background: '#000',
        color: '#fff',
        gap: '1rem',
        padding: '2rem',
        textAlign: 'center'
      }}>
        <div style={{ fontSize: '3rem' }}>🔍</div>
        <div style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>{error}</div>
        <div style={{ opacity: 0.7, maxWidth: '500px' }}>
          Looking for username: "{shopUsername}"
        </div>
        <button
          onClick={() => redirectToMainApp()}
          style={{
            background: '#800000',
            color: 'white',
            border: 'none',
            padding: '0.75rem 1.5rem',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '1rem',
            fontWeight: '600',
            marginTop: '1rem'
          }}
        >
          Go to Main Site
        </button>
        <button
          onClick={() => window.location.reload()}
          style={{
            background: 'transparent',
            color: '#800000',
            border: '1px solid #800000',
            padding: '0.5rem 1rem',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '0.9rem'
          }}
        >
          Retry
        </button>
      </div>
    );
  }

  // Redirect immediately when shopId is found
  if (shopId) {
  return <Navigate to={`/shop/${shopId}/view`} replace />;
}

// Show loading skeleton while fetching
return (
  <div style={{
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100vh',
    background: '#000'
  }}>
    <div style={{
      width: '40px',
      height: '40px',
      border: '3px solid rgba(128, 0, 0, 0.3)',
      borderTop: '3px solid #800000',
      borderRadius: '50%',
      animation: 'spin 1s linear infinite'
    }}/>
    <style>{`
      @keyframes spin {
        to { transform: rotate(360deg); }
      }
    `}</style>
  </div>
);
};

// Route Guard for Subdomain
const SubdomainRouteGuard = ({ children }) => {
  const location = useLocation();
  const subdomainInfo = parseSubdomain();

  // If on subdomain and trying to access non-allowed route, redirect to main app
  if (subdomainInfo.isSubdomain && !isSubdomainAllowedRoute(location.pathname)) {
    redirectToMainApp(location.pathname);
    return null;
  }

  return children;
};

// Main App Routes Component
const AppRoutes = () => {
  const location = useLocation();
  const { isAuthenticated } = useAuth();
  const subdomainInfo = parseSubdomain();

  const SW_UPDATE_KEY = 'sw_last_update';
const MIN_UPDATE_INTERVAL = 3600000; // 1 hour

const shouldUpdateServiceWorker = () => {
  const lastUpdate = localStorage.getItem(SW_UPDATE_KEY);
  if (!lastUpdate) return true;
  
  return Date.now() - parseInt(lastUpdate) > MIN_UPDATE_INTERVAL;
};

const isBrave = async () => {
  return (navigator.brave && await navigator.brave.isBrave()) || false;
};

// REPLACE the existing service worker useEffect with this:
useEffect(() => {
  const initServiceWorker = async () => {
    if (!('serviceWorker' in navigator)) return;
    
    // Check if Brave
    const isBraveBrowser = await isBrave();
    if (isBraveBrowser) {
      console.log('⚠️ Brave detected - using optimized loading');
    }
    
    // Only update if enough time has passed
    if (!shouldUpdateServiceWorker()) {
      console.log('⏭️ Skipping service worker update (too recent)');
      return;
    }
    
    try {
      const registrations = await navigator.serviceWorker.getRegistrations();
      
      // Update only the first registration, not all
      if (registrations.length > 0) {
        await registrations[0].update();
        localStorage.setItem(SW_UPDATE_KEY, Date.now().toString());
        console.log('🔄 Service Worker updated');
      }
    } catch (error) {
      console.error('Service Worker update failed:', error);
    }
  };

  // Delay initialization slightly to not block initial render
  const timeout = setTimeout(initServiceWorker, 1000);
  
  return () => clearTimeout(timeout);
}, []);

useEffect(() => {
  if (!('serviceWorker' in navigator)) return;

  const handleMessage = (event) => {
    if (!event.data) return;

    if (event.data.type === 'NAVIGATE_TO') {
      console.log('🔄 Navigating to:', event.data.url);
      if (window.location.pathname !== event.data.url) {
        window.location.href = event.data.url;
      }
    }

    if (event.data.type === 'SW_UPDATED') {
      console.log('🔄 New service worker activated — reloading for fresh content');
      window.location.reload();
    }
  };

  navigator.serviceWorker.addEventListener('message', handleMessage);
  return () => navigator.serviceWorker.removeEventListener('message', handleMessage);
}, []);

useEffect(() => {
  const unsubscribe = auth.onAuthStateChanged((user) => {
    if (user && !user.isAnonymous) {
      // ✅ Real user logged in - clear any guest sessions
      const guestSession = localStorage.getItem('guest_session');
      if (guestSession) {
        console.log('🧹 Clearing guest session - real user logged in');
        localStorage.removeItem('guest_session');
      }
    }
  });

  return () => unsubscribe();
}, []);
  
  useEffect(() => {
  let isMounted = true;
  
  const initializeFCMWithTimeout = async (user) => {
    if (!isMounted) return;
    
    const notificationMode = localStorage.getItem('notification_mode');
    
    if (notificationMode === 'in-app-only') {
      console.log('ℹ️ Using in-app notifications only');
      return;
    }
    
    // ✅ UPDATED: Check per-device cache
    const deviceId = NotificationService.getDeviceId();
    const lastInit = localStorage.getItem(`fcm_init_timestamp_${user.uid}_${deviceId}`);
    
    if (lastInit) {
      const timeSinceInit = Date.now() - parseInt(lastInit);
      if (timeSinceInit < 3600000) { // 1 hour
        console.log('⏭️ FCM already initialized for this device recently');
        return;
      }
    }
    
    console.log('👤 User logged in, initializing FCM for this device...');
    
    if ('Notification' in window && Notification.permission === 'granted') {
      try {
        const timeout = new Promise((_, reject) => 
          setTimeout(() => reject(new Error('FCM initialization timeout')), 15000)
        );
        
        const tokenPromise = NotificationService.initializeFCM();
        const token = await Promise.race([tokenPromise, timeout]);
        
        if (token && isMounted) {
          console.log('✅ FCM initialized successfully for this device');
          await NotificationService.saveFCMToken(user.uid, token);
          NotificationService.setupForegroundMessageHandler();
          
          // ✅ UPDATED: Store per-device timestamp
          localStorage.setItem(`fcm_init_timestamp_${user.uid}_${deviceId}`, Date.now().toString());
          localStorage.setItem('notification_mode', 'push-enabled');
          
          // ✅ NEW: Cleanup old tokens
          await NotificationService.cleanupInvalidTokens(user.uid);
          
        } else if (isMounted) {
          console.log('ℹ️ FCM token not obtained - using in-app only');
          localStorage.setItem('notification_mode', 'in-app-only');
        }
      } catch (error) {
        if (error.message.includes('timeout')) {
          console.warn('⏱️ FCM initialization timed out - using in-app only');
          localStorage.setItem('notification_mode', 'in-app-only');
        } else {
          console.error('❌ Error initializing FCM:', error);
          localStorage.setItem('notification_mode', 'in-app-only');
        }
      }
    } else {
      console.log('ℹ️ Notification permission not granted - using in-app only');
      localStorage.setItem('notification_mode', 'in-app-only');
    }
  };

  const unsubscribe = auth.onAuthStateChanged(async (user) => {
    if (user && isMounted) {
      setTimeout(() => initializeFCMWithTimeout(user), 3000);
    }
  });

  return () => {
    isMounted = false;
    unsubscribe();
  };
}, []);
  
  // Rest of your existing AppRoutes code...
  const hideNavRoutes = ['/auth', '/verify-email', '/shop/create/template'];
  const isHideNavRoute = hideNavRoutes.some(
    path => location.pathname === path || location.pathname.includes(path)
  );
  
  const showDesktopNavMenu = !subdomainInfo.isSubdomain && isAuthenticated && !isHideNavRoute;
  const showBottomNav = !subdomainInfo.isSubdomain && isAuthenticated && !isHideNavRoute;
  const needsBottomPadding = showBottomNav && window.innerWidth <= 768;

  return (
    <SubdomainRouteGuard>
      <div style={{
        // Mirrors BottomNavContainer's fluid height (clamp(60px, 17.5vw, 74px))
        // so reserved content padding always matches the nav's actual size.
        paddingBottom: needsBottomPadding ? 'calc(clamp(60px, 17.5vw, 74px) + env(safe-area-inset-bottom, 0px))' : '0',
        minHeight: '100vh'
      }}>
        {showDesktopNavMenu && <NavMenu theme={DEFAULT_THEME} />}
        
        {subdomainInfo.isSubdomain ? (
          <Routes>
            <Route 
              path="/*" 
              element={<SubdomainHandler shopUsername={subdomainInfo.shopUsername} />} 
            />
            <Route 
              path="/shop/:shopId/view" 
              element={<ShopPublicView />} 
            />
          </Routes>
        ) : (
          <Routes>
            <Route path="/" element={<WelcomePage />} />
            <Route path="/auth" element={<AuthPage />} />
            <Route path="/shop/create/template" element={<LiveShopCreation />} />
            <Route path="/shop/:userId" element={<ShopPage />} />
            <Route path="/shop/:shopId/view" element={<ShopPublicView />} />
            
            <Route path="/messages" element={
              <ProtectedRoute allowGuest={true}>
                <MessagesPage />
              </ProtectedRoute>
            } />

            <Route path="/notifications" element={
              <ProtectedRoute>
                <NotificationsPage />
              </ProtectedRoute>
            } />

            <Route path="/admin/dashboard" element={
              <ProtectedRoute>
                <ShopDashboard />
              </ProtectedRoute>
            } />
            
            <Route path="/verify-email" element={
              <ProtectedRoute requireVerification={false}>
                <VerifyEmail />
              </ProtectedRoute>
            } />

            <Route path="/profile" element={
              <ProtectedRoute>
                <ProfilePage />
              </ProtectedRoute>
            } />

            <Route path="/discover" element={<WelcomePage />} />
          </Routes>
        )}

        {showBottomNav && <BottomNavigation theme={DEFAULT_THEME} />}
      </div>
    </SubdomainRouteGuard>
  );
};

function App() {
  return (
    <LocationProvider>
      <Router>
        <AuthProvider>
          <TempStoreProvider>
            <ActiveShopProvider>
              <GlobalStyles />
              <PWASessionStatus />
              <Analytics /> {/* ADDED: Vercel Analytics Component */}
              <AppRoutes />
            </ActiveShopProvider>
          </TempStoreProvider>
        </AuthProvider>
      </Router>
    </LocationProvider>
  );
}

export default App;